import { Box, Link, Typography } from '@mui/material';
import { Helmet } from 'react-helmet';
import { StoryPageLayout } from '../layouts';

/**
 * Amazon RDS for SQL Server Article
 *
 * Blog-style article covering the re:Invent 2025 announcements for RDS SQL Server.
 *
 * @returns {import('react').ReactElement} RDS SQL Server Article page
 */
function RDSSQLServerArticle() {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline:
      'Amazon RDS for SQL Server Just Got a Lot Cheaper (And Better Too!)',
    description:
      'AWS re:Invent 2025 brought major cost-saving features for SQL Server on RDS including optimized CPU licensing, Developer Edition support, and expanded storage options.',
    author: {
      '@type': 'Organization',
      name: 'RSM Collective',
    },
    publisher: {
      '@type': 'Organization',
      name: 'RSM Collective',
      logo: {
        '@type': 'ImageObject',
        url: 'https://www.rsmc.ai/images/r-logo.png',
      },
    },
    datePublished: '2025-12-17',
    dateModified: '2025-12-17',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': 'https://www.rsmc.ai/articles/rds-sql-server-reinvent-2025',
    },
  };

  // Common paragraph styling
  const paragraphSx = {
    fontSize: '1rem',
    lineHeight: 1.8,
    color: 'var(--color-text)',
    mb: 3,
  };

  // Section heading styling
  const headingSx = {
    fontWeight: 700,
    mb: 3,
    mt: 5,
    color: 'var(--color-heading)',
    fontSize: { xs: '1.25rem', md: '1.5rem' },
  };

  // Link styling
  const linkSx = {
    color: '#5c6bc0',
    textDecoration: 'underline',
    '&:hover': {
      color: '#3949ab',
    },
  };

  return (
    <>
      <Helmet>
        <title>
          Amazon RDS for SQL Server Just Got a Lot Cheaper (And Better Too!) |
          RSM Collective
        </title>
        <meta
          name="description"
          content="AWS re:Invent 2025 brought major cost-saving features for SQL Server on RDS including optimized CPU licensing, Developer Edition support, and expanded storage options."
        />
        <meta
          property="og:title"
          content="Amazon RDS for SQL Server Just Got a Lot Cheaper (And Better Too!)"
        />
        <meta
          property="og:description"
          content="AWS re:Invent 2025 brought major cost-saving features for SQL Server on RDS including optimized CPU licensing, Developer Edition support, and expanded storage options."
        />
        <meta property="og:type" content="article" />
        <meta
          property="og:url"
          content="https://www.rsmc.ai/articles/rds-sql-server-reinvent-2025"
        />
        <meta property="og:image" content="https://www.rsmc.ai/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://www.rsmc.ai/og-image.png" />
        <meta
          name="twitter:title"
          content="Amazon RDS for SQL Server Just Got a Lot Cheaper"
        />
        <meta
          name="twitter:description"
          content="re:Invent 2025 announcements for SQL Server cost savings on AWS RDS"
        />
        <meta
          name="keywords"
          content="Amazon RDS, SQL Server, AWS, re:Invent 2025, database, cost optimization, cloud migration, SMT, Developer Edition"
        />
        <link
          rel="canonical"
          href="https://www.rsmc.ai/articles/rds-sql-server-reinvent-2025"
        />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <StoryPageLayout
        title="Amazon RDS for SQL Server Just Got Better"
        subtitle=""
        formHeading="Request Your Modernization Assessment"
        formFilePath="/forms/discuss-project-form.txt"
        heroImage="/images/1000004809.jpg"
        heroImageAlt="AWS re:Invent community graffiti wall covered with company names and attendee signatures under purple venue lighting"
        errorMessage="Unable to load contact form"
        loadingMessage="Loading contact form..."
      >
        <Box component="article">
          {/* Article Title */}
          <Typography
            variant="h1"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '1.5rem', md: '1.75rem' },
              color: 'var(--color-heading)',
              mb: 4,
            }}
          >
            Amazon RDS for SQL Server Just Got a Lot Cheaper (And Better Too!)
          </Typography>

          {/* Introduction */}
          <Typography sx={paragraphSx}>
            One of the oldest and most popular services on offer from AWS is the
            Amazon Relational Database Service, or RDS. Introduced in 2009, the
            managed database service exemplifies the value that cloud offers to
            eliminate "undifferentiated heavy lifting" for customers. RDS
            abstracts away the complexities of configuration, backups, high
            availability and disaster recovery, allowing users to focus on their
            business needs. Three years after debuting with managed MySQL, the
            company started offering Amazon RDS for SQL Server in 2012.
            Companies with software stacks built around SQL Server were able to
            migrate workloads to the cloud and gain the advantages of a managed
            service.
          </Typography>

          {/* SQL Server - The RDS Red-Headed Stepchild */}
          <Typography variant="h2" sx={headingSx}>
            SQL Server - The RDS Red-Headed Stepchild
          </Typography>

          <Typography sx={paragraphSx}>
            Despite the fact that SQL Server has been a supported platform on
            RDS for the past 13 years, it's always stuck out from the other
            database engines on offer. The single biggest reason for this is
            simple: cost. If you compare MySQL or Postgres pricing with SQL
            Server on identical instance types, the cost of running SQL Server
            can be more than 6x higher! The difference in price arises from
            licensing both SQL Server and Windows. Even if you compare SQL
            Server against another proprietary database like Oracle, the sticker
            shock is pretty significant at more than 2.5x more expensive for the
            Microsoft product. But if you needed SQL Server, the price of those
            other database engines was almost irrelevant. Unless a company was
            willing to refactor their code to use an open source database there
            wasn't a good alternative in AWS without a completely self-managed
            EC2 solution.
          </Typography>

          <Typography sx={paragraphSx}>
            Price hasn't been the only issue for SQL Server users, though. For
            many years the maximum storage capacity for a SQL Server RDS
            instance topped out at 16 TiB while the open source database engines
            were able to scale up to 64TiB. It wasn't until June of 2024 that
            AWS announced SQL Server RDS instances would be able to scale up to
            64 TiB, but that was simply the consequence of new EBS volume types
            with 4 times the previous capacity. This meant that Postgres, MySQL
            and MariaDB instances could scale up to 256 TiB. Those other engines
            also had more flexibility in the cost and performance of their
            storage with the ability to specify up to 4 separate storage volumes
            with different types (gp3 or io2) and different provisioned IOPS for
            each volume. Data that required maximum performance could be stored
            on io2 volumes with up to 256,000 provisioned IOPS while less
            performance-sensitive data could be stored on far cheaper gp3
            volumes all within the same database instance.
          </Typography>

          <Typography sx={paragraphSx}>
            Amazon was well aware of the shortcomings of SQL Server for RDS and
            developed tools to help customers migrate away from SQL Server to
            Postgres. In 2020 they announced the development of Babelfish, a
            compatibility layer in PostgreSQL that allows SQL Server's SQL
            variant, TSQL, to run natively. They also released tools to convert
            schema and queries from SQL Server to Postgres. All of this, though,
            still required customers to refactor and test their applications
            against this new system. Perhaps even more important to many
            organizations, it meant a whole new set of skills and experience was
            needed to administer, tune and maintain the database.
          </Typography>

          <Typography sx={paragraphSx}>
            Knowing that some customers would not migrate away from SQL Server,
            AWS introduced the ability to bring your own license to RDS,
            allowing customers with existing relationships with Microsoft to
            save money on the license side. They also provided training for
            customers who were interested in running SQL Server on EC2
            instances, showing them how to achieve the best performance with the
            lowest licensing costs. This included tips like disabling SMT and
            setting up multiple EBS volumes.
          </Typography>

          {/* re:Invent 2025 - RDS for SQL Server Comes of Age */}
          <Typography variant="h2" sx={headingSx}>
            re:Invent 2025 - RDS for SQL Server Comes of Age
          </Typography>

          <Typography sx={paragraphSx}>
            December 2, 2025 might just be Christmas if you've been using SQL
            Server on RDS. AWS made three announcements at re:Invent 2025 that
            should drastically reduce your costs as well as bring storage parity
            with the other database engines available in RDS.
          </Typography>

          <Typography sx={paragraphSx}>
            Let's start with the feature that's likely to have the biggest
            impact on cost:{' '}
            <Link
              href="https://aws.amazon.com/about-aws/whats-new/2025/12/amazon-rds-sql-server-optimized-cpu-lower-prices/"
              target="_blank"
              rel="noopener noreferrer"
              sx={linkSx}
            >
              m7i & r7i instance types with Optimize CPU features
            </Link>
            . RDS for SQL Server on these new instance types will automatically
            disable simultaneous multi-threading (SMT) on in the CPU, cutting
            the number of vCPUs in half and thus reducing the licensing costs in
            half as well. Workloads like database engines don't gain much from
            SMT because additional threads tend to compete for the same
            resources in the system like cache and RAM. For workloads that are
            particularly IO or memory intensive, users can even decrease the
            number of active CPU cores further and gain more savings on
            licensing. Anyone using RDS for SQL Server on a 2xlarge or bigger
            instance should probably upgrade as soon as possible. It's not often
            that you get a 50% cost reduction opportunity with so little work.
          </Typography>

          <Typography sx={paragraphSx}>
            The second cost-saving feature announced at re:Invent is the
            availability of{' '}
            <Link
              href="https://aws.amazon.com/about-aws/whats-new/2025/12/amazon-rds-sql-server-supports-developer-edition/"
              target="_blank"
              rel="noopener noreferrer"
              sx={linkSx}
            >
              Developer Edition licensing for non-production workloads
            </Link>
            . Microsoft has long offered the free Developer Edition to users but
            only now is it available for RDS customers. This allows development,
            testing and integration environments to completely eliminate the SQL
            Server licensing cost entirely, but Windows license costs will
            remain. Given that SQL Server licenses are about 2.5x to 8x more
            expensive per CPU than Windows licenses, this represents a major
            savings opportunity as well. Anything that brings down the cost of
            development and testing likely represents an even bigger gain than
            just the operational expense, though, as it helps to encourage
            better software development practices.
          </Typography>

          <Typography sx={paragraphSx}>
            The third announcement offers up both cost-saving potential as well
            as genuinely new capabilities. RDS for SQL Server now supports{' '}
            <Link
              href="https://aws.amazon.com/about-aws/whats-new/2025/12/rds-oracle-sql-server-256-tib-storage-support/"
              target="_blank"
              rel="noopener noreferrer"
              sx={linkSx}
            >
              additional storage volumes and a maximum of 256 TiB total storage
            </Link>
            . This brings parity with Postgres, MySQL and MariaDB. Cost savings
            here comes from the ability to mix and match storage volume types so
            that performance-sensitive data can reside on io2 provisioned IOPS
            storage while other data can live on cheaper gp3 volumes. For many
            use cases this can represent a major opportunity for optimization of
            both cost and query performance but will certainly take more effort
            to get the best out of the new offering compared to the other
            announcements.
          </Typography>

          {/* 2026 and Beyond */}
          <Typography variant="h2" sx={headingSx}>
            2026 and Beyond
          </Typography>

          <Typography sx={paragraphSx}>
            These SQL Server-specific enhancements go hand in hand with another
            announcement at re:Invent 2025, the availability of{' '}
            <Link
              href="https://aws.amazon.com/blogs/aws/introducing-database-savings-plans-for-aws-databases/"
              target="_blank"
              rel="noopener noreferrer"
              sx={linkSx}
            >
              Database Savings Plans
            </Link>
            . Like Savings Plans for EC2 usage, these give users an alternative
            and more flexible way to reduce costs compared to purchasing
            reserved instances. Since these savings plans can be used across
            database services they offer a clear path for those still aiming to
            migrate workloads off of SQL Server entirely. Despite these recent
            improvements, SQL Server still remains far more expensive than
            Postgres or MySQL, so there is a big incentive to move to these open
            source alternatives.
          </Typography>

          {/* Visual separator - R logo */}
          <Box
            sx={{
              textAlign: 'center',
              mt: 6,
              mb: 2,
            }}
          >
            <Box
              component="img"
              src="/images/r-logo.png"
              alt="RSM Collective"
              sx={{
                width: '50px',
                height: 'auto',
                opacity: 0.7,
              }}
            />
          </Box>
        </Box>
      </StoryPageLayout>
    </>
  );
}

export default RDSSQLServerArticle;
